'use client';

import { useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import {
  additionalCostsMessages,
  deliveryMessages,
  paymentMethodsMessages,
  serviceInformationCardMessages,
} from './ServiceInformationCard.client.messages';
import { AdditionalInfoItem } from '../AdditionalInfoItem/AdditionalInfoItem.client';
import { getValidInfoList } from '../getValidInfoList/getValidInfoList';
import { type RawProductData } from '../../../types';

export type ServiceInformationCardProps = {
  additionalCostsData: NonNullable<RawProductData['additionalInfo']>['additionalCosts'];
  deliveryData: NonNullable<RawProductData['additionalInfo']>['delivery'];
};

const defaultClasses = 'text-color-text-dark size-3';

export const ServiceInformationCard = ({
  additionalCostsData,
  deliveryData,
}: ServiceInformationCardProps) => {
  const { formatMessage } = useIntl();
  const {
    product: {
      serviceLayer: { paymentMethods },
    },
  } = useConfig();

  if (additionalCostsData === null && deliveryData === null && paymentMethods === null) return null;

  if (
    additionalCostsData?.infoList.length === 0 &&
    deliveryData?.infoList.length === 0 &&
    paymentMethods === null
  ) {
    return null;
  }

  const validAdditionalCostsInfoList = additionalCostsData
    ? getValidInfoList(additionalCostsData.infoList, additionalCostsMessages)
    : [];
  const validDeliveryInfoList = deliveryData
    ? getValidInfoList(deliveryData.infoList, deliveryMessages)
    : [];

  if (
    validAdditionalCostsInfoList.length === 0 &&
    validDeliveryInfoList.length === 0 &&
    paymentMethods === null
  )
    return null;

  return (
    <Card>
      <CardHeader hasDivider>
        {formatMessage(serviceInformationCardMessages.cardHeadline)}
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-start gap-1 pb-2">
          {paymentMethods !== null && (
            <AdditionalInfoItem
              icon={<span className={`icon-emp-paymentmethods ${defaultClasses}`} />}
              serviceLayerId={paymentMethods}
              text={formatMessage(paymentMethodsMessages.paymentMethods)}
            />
          )}
          {validDeliveryInfoList.map((item) => (
            <AdditionalInfoItem
              key={item.translationKey}
              icon={item.iconUrl || <span className={`icon-emp-in-delivery ${defaultClasses}`} />}
              serviceLayerId={item.serviceLayerId}
              text={formatMessage(item.messageDescriptor)}
            />
          ))}
          {validAdditionalCostsInfoList.map((item) => (
            <AdditionalInfoItem
              key={item.translationKey}
              icon={item.iconUrl || <span className={`icon-emp-return ${defaultClasses}`} />}
              serviceLayerId={item.serviceLayerId}
              text={formatMessage(item.messageDescriptor)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
